import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getUserEventRegistrations, cancelEventRegistration } from '../services/api';
import Alert from '../components/common/Alert';

function MyRegistrations({ user }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    loadRegistrations();
  }, [user]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const data = await getUserEventRegistrations();
      const regs = data.results || data;
      // /event-registrations/ returns every registration in the system for
      // staff/admins, not just the caller's own - filter to the current
      // user or an admin would see (and be able to cancel) everyone else's
      // real registrations on their own "My Registrations" page.
      setRegistrations(regs.filter(reg => reg.user === user?.id));
    } catch (err) {
      setError('Failed to load registrations');
      console.error('Failed to load registrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRegistration = async (registration) => {
    const confirmMessage = registration.price_paid > 0
      ? `Are you sure you want to cancel this registration? $${Number(registration.price_paid).toFixed(2)} will be added to your account as credit. If you'd like a cash refund instead, please contact support.`
      : 'Are you sure you want to cancel this registration?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setCancelling(registration.id);
      setError('');
      setSuccess('');

      const response = await cancelEventRegistration(registration.id);

      // Show success message with refund info
      if (response.refund) {
        setSuccess(response.refund.message);
      } else {
        setSuccess('Registration cancelled successfully');
      }

      // Reload registrations
      await loadRegistrations();
    } catch (err) {
      setError(err.message || 'Failed to cancel registration');
    } finally {
      setCancelling(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'registered':
        return 'bg-green-100 text-green-800';
      case 'pending_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'attended':
        return 'bg-blue-100 text-blue-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">My Event Registrations</h1>
        <p className="mt-2 text-gray-600">View your event registration history</p>
      </div>

      <Alert type="error" message={error} onClose={() => setError('')} />
      <Alert type="success" message={success} onClose={() => setSuccess('')} />

      {registrations.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-600 mb-4">You have no event registrations yet.</p>
          <Link
            to="/events"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Browse Events
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {registrations.map((registration) => (
            <div key={registration.id} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <Link
                    to={`/events/${registration.event}`}
                    className="text-xl font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {registration.event_name}
                  </Link>
                  {registration.attendee_name && (
                    <p className="text-gray-700 text-sm mt-1">
                      <span className="font-medium">Attendee:</span> {registration.attendee_name}
                      {registration.is_for_associate && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          Associate Member
                        </span>
                      )}
                    </p>
                  )}
                  {registration.event_details && (
                    <p className="text-gray-600 text-sm mt-1">
                      {new Date(registration.event_details.start_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
                {registration.status === 'cancelled' ? (
                  <Link
                    to={`/events/${registration.event}`}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                  >
                    Register
                  </Link>
                ) : (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(registration.status)}`}>
                    {formatStatus(registration.status)}
                  </span>
                )}
              </div>

              <div className="border-t pt-4 mt-4">
                {registration.status === 'registered' && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Registration Code: <span className="font-medium">{registration.registration_code}</span>
                    </p>
                    {registration.discount_amount > 0 ? (
                      <>
                        <p className="text-sm text-gray-600">
                          Original Price: <span className="line-through">${Number(registration.original_price).toFixed(2)}</span>
                        </p>
                        <p className="text-sm text-green-600">
                          Discount: -${Number(registration.discount_amount).toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Paid: <span className="font-medium">${Number(registration.price_paid).toFixed(2)}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-gray-600">
                        {registration.price_paid > 0 ? (
                          <>Amount Paid: <span className="font-medium">${Number(registration.price_paid).toFixed(2)}</span></>
                        ) : (
                          <span className="text-green-600 font-medium">Free Event</span>
                        )}
                      </p>
                    )}
                    {registration.can_cancel && (
                      <div className="mt-4 pt-4 border-t">
                        <button
                          onClick={() => handleCancelRegistration(registration)}
                          disabled={cancelling === registration.id}
                          className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
                        >
                          {cancelling === registration.id ? 'Cancelling...' : 'Cancel Registration'}
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          {registration.price_paid > 0
                            ? `Paid events can be cancelled up to 7 days before. Refund will be added as account credits.`
                            : `Free events can be cancelled anytime before the event starts.`}
                        </p>
                      </div>
                    )}
                    {!registration.can_cancel && registration.cancel_reason && (
                      <p className="text-xs text-gray-500 mt-2">
                        {registration.cancel_reason}
                      </p>
                    )}
                  </div>
                )}
                {registration.status === 'cancelled' && (
                  <p className="text-sm text-gray-600">Your registration was cancelled.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyRegistrations;
